import React, { ComponentPropsWithoutRef, useState } from 'react';
import { ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useChannelSlug } from '@shared/util/navigation';
import { MBAttachmentButton } from '../containers/attachmentButton';
import { MBAttachmentList } from '../containers/attachmentList';
import { MBChatInputPlusButton } from '../containers/plusButton';
import { MBChatInputSendButton } from '../containers/sendButton';
import { MBEmojiButton } from './emojiButton';
import { Styles } from './styles';

export interface ChatInputBarProps {
  draftId: string;
  value: string;
  isEditing: boolean;
  hasAttachments: boolean;
  placeholder?: string;
  hideUrgent?: boolean;
  autoFocus?: boolean;
  innerRef?: React.RefObject<MBAutosizeTextInput>;
  onBlur?(): void;
  onFocus?(): void;
  onChangeText(text: string): void;
  onCursorPositionChange?(position: number): void;
  onPasteFiles(files: File[]): void;
  onSend(): void;
  onSubmitEditing(): void;
  onCancelEdit(): void;
  onAddEmoji(emoji: string): void;
}

export function ChatInputBar({
  draftId,
  value,
  isEditing,
  hasAttachments,
  placeholder,
  hideUrgent,
  autoFocus,
  innerRef,
  onBlur,
  onFocus,
  onChangeText,
  onCursorPositionChange,
  onPasteFiles,
  onSend,
  onSubmitEditing,
  onCancelEdit,
  onAddEmoji,
}: ChatInputBarProps) {
  const [sending, setSending] = useState(false);
  const canSend = !!value.length || hasAttachments;

  const send = async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      onSend();
    } finally {
      setSending(false);
    }
  };

  const change = (text: string) => {
    if (!sending) {
      onChangeText(text);
    }
  };

  return (
    <MBView paddingVertical={0.625} style={Styles.container}>
      <View row gap={1} shrinkWrap fullWidth style={{ alignItems: 'flex-end' }}>
        {!isEditing && (
          <View center flex={0} style={{ minHeight: grid(6.5) }}>
            <MBPlusButtonWrapped draftId={draftId} hideUrgent={hideUrgent} />
          </View>
        )}
        {isEditing && <View paddingLeft={1} />}

        <MBView flex style={[Styles.inputContainer, chatInputAnchorStyle]}>
          <MBAttachmentList draftId={draftId} />
          <View row relative>
            <MBAutosizeTextInput
              style={{
                flexGrow: 1,
                paddingVertical: grid(1),
                paddingLeft: 0,
                paddingRight: grid(5),
              }}
              ref={innerRef}
              placeholder={placeholder}
              size="normal"
              autoFocus={autoFocus}
              padding="normal"
              value={value}
              onChangeText={change}
              maxRows={10}
              onPasteFiles={onPasteFiles}
              onSubmitEditing={onSubmitEditing}
              onCursorPositionChange={onCursorPositionChange}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={sending}
            />
          </View>
          <View style={{ position: 'absolute', right: 0, bottom: 1 }}>
            <MBEmojiButton onSelect={onAddEmoji} />
          </View>
        </MBView>

        <View center flex={0} style={{ minHeight: grid(6.5) }}>
          {canSend && (
            <MBChatInputSendButton draftId={draftId} onClick={send} testID="chat-send-button" />
          )}
          {!canSend && <MBAttachmentButtonWrapped draftId={draftId} />}
        </View>
      </View>

      {isEditing && (
        <MBView row wrap style={Styles.cancelButton}>
          <MBTouchableOpacity onPress={onCancelEdit} style={{ paddingHorizontal: 5 }}>
            <Text>{i18n('common.cancel')}</Text>
          </MBTouchableOpacity>
        </MBView>
      )}
    </MBView>
  );
}

const chatInputAnchorStyle = { anchorName: '--chat-input' } as ViewStyle;

function MBAttachmentButtonWrapped({
  draftId,
}: Omit<ComponentPropsWithoutRef<typeof MBAttachmentButton>, 'validateUploads'>) {
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return <MBAttachmentButton draftId={draftId} validateUploads={validateUploads} />;
}

function MBPlusButtonWrapped({
  draftId,
  hideUrgent,
}: Omit<ComponentPropsWithoutRef<typeof MBChatInputPlusButton>, 'validateUploads'>) {
  const channelSlug = useChannelSlug();
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return (
    <MBChatInputPlusButton
      channelSlug={channelSlug}
      draftId={draftId}
      hideUrgent={hideUrgent}
      validateUploads={validateUploads}
    />
  );
}
